import { priceAdjustmentType } from 'Models/Enums';
import * as uuid from 'uuid';

/**
 * Days of the week flags enum for occurrence rules.
 */
export enum DaysOfWeekFlags {
	None = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 4,
	Thursday = 8,
	Friday = 16,
	Saturday = 32,
	Sunday = 64,
}

/**
 * Interface for OccurrenceRuleEntity.
 */
export interface IOccurrenceRuleEntity {
	id: string;
	eventSeriesId: string;
	daysOfWeek: DaysOfWeekFlags;
	startTime: string; // "HH:mm:ss"
	endTime: string; // "HH:mm:ss"
	adjustmentType: priceAdjustmentType;
	adjustmentValue: number;
}

/**
 * Creates a new empty occurrence rule with default values.
 */
export function createEmptyOccurrenceRule(): IOccurrenceRuleEntity {
	return {
		id: uuid.v4(),
		eventSeriesId: '',
		daysOfWeek: DaysOfWeekFlags.None,
		startTime: '09:00:00',
		endTime: '17:00:00',
		adjustmentType: 'NONE',
		adjustmentValue: 0,
	};
}

/**
 * Helper function to check if a day flag is set.
 */
export function isDayFlagSet(flags: DaysOfWeekFlags, day: DaysOfWeekFlags): boolean {
	// eslint-disable-next-line no-bitwise
	return (flags & day) !== 0;
}

/**
 * Helper function to toggle a day flag.
 */
export function toggleDayFlag(flags: DaysOfWeekFlags, day: DaysOfWeekFlags): DaysOfWeekFlags {
	if (isDayFlagSet(flags, day)) {
		// eslint-disable-next-line no-bitwise
		return flags & ~day;
	}
	// eslint-disable-next-line no-bitwise
	return flags | day;
}

/**
 * Helper function to get an array of selected days from flags.
 */
export function getSelectedDays(flags: DaysOfWeekFlags): DaysOfWeekFlags[] {
	const days: DaysOfWeekFlags[] = [];
	/* eslint-disable no-bitwise */
	if (flags & DaysOfWeekFlags.Monday) days.push(DaysOfWeekFlags.Monday);
	if (flags & DaysOfWeekFlags.Tuesday) days.push(DaysOfWeekFlags.Tuesday);
	if (flags & DaysOfWeekFlags.Wednesday) days.push(DaysOfWeekFlags.Wednesday);
	if (flags & DaysOfWeekFlags.Thursday) days.push(DaysOfWeekFlags.Thursday);
	if (flags & DaysOfWeekFlags.Friday) days.push(DaysOfWeekFlags.Friday);
	if (flags & DaysOfWeekFlags.Saturday) days.push(DaysOfWeekFlags.Saturday);
	if (flags & DaysOfWeekFlags.Sunday) days.push(DaysOfWeekFlags.Sunday);
	/* eslint-enable no-bitwise */
	return days;
}

/**
 * Helper function to get the label for a day flag.
 */
export function getDayLabel(day: DaysOfWeekFlags): string {
	switch (day) {
		case DaysOfWeekFlags.Monday:
			return 'Mon';
		case DaysOfWeekFlags.Tuesday:
			return 'Tue';
		case DaysOfWeekFlags.Wednesday:
			return 'Wed';
		case DaysOfWeekFlags.Thursday:
			return 'Thu';
		case DaysOfWeekFlags.Friday:
			return 'Fri';
		case DaysOfWeekFlags.Saturday:
			return 'Sat';
		case DaysOfWeekFlags.Sunday:
			return 'Sun';
		default:
			return '';
	}
}

/**
 * Helper function to get the full label for a day flag.
 */
export function getDayFullLabel(day: DaysOfWeekFlags): string {
	switch (day) {
		case DaysOfWeekFlags.Monday:
			return 'Monday';
		case DaysOfWeekFlags.Tuesday:
			return 'Tuesday';
		case DaysOfWeekFlags.Wednesday:
			return 'Wednesday';
		case DaysOfWeekFlags.Thursday:
			return 'Thursday';
		case DaysOfWeekFlags.Friday:
			return 'Friday';
		case DaysOfWeekFlags.Saturday:
			return 'Saturday';
		case DaysOfWeekFlags.Sunday:
			return 'Sunday';
		default:
			return '';
	}
}

/**
 * All days of the week for iteration.
 */
export const ALL_DAYS: DaysOfWeekFlags[] = [
	DaysOfWeekFlags.Monday,
	DaysOfWeekFlags.Tuesday,
	DaysOfWeekFlags.Wednesday,
	DaysOfWeekFlags.Thursday,
	DaysOfWeekFlags.Friday,
	DaysOfWeekFlags.Saturday,
	DaysOfWeekFlags.Sunday,
];

/**
 * Helper function to format days of week flags as a readable string.
 */
export function formatDaysOfWeek(flags: DaysOfWeekFlags): string {
	const selectedDays = getSelectedDays(flags);
	if (selectedDays.length === 0) return 'No days selected';
	if (selectedDays.length === 7) return 'Every day';

	// Check for weekdays (Mon-Fri)
	const weekdays = [
		DaysOfWeekFlags.Monday,
		DaysOfWeekFlags.Tuesday,
		DaysOfWeekFlags.Wednesday,
		DaysOfWeekFlags.Thursday,
		DaysOfWeekFlags.Friday,
	];
	const isWeekdays =
		weekdays.every(d => isDayFlagSet(flags, d)) &&
		!isDayFlagSet(flags, DaysOfWeekFlags.Saturday) &&
		!isDayFlagSet(flags, DaysOfWeekFlags.Sunday);
	if (isWeekdays) return 'Weekdays';

	// Check for weekend (Sat-Sun)
	const isWeekend =
		isDayFlagSet(flags, DaysOfWeekFlags.Saturday) &&
		isDayFlagSet(flags, DaysOfWeekFlags.Sunday) &&
		selectedDays.length === 2;
	if (isWeekend) return 'Weekends';

	return selectedDays.map(d => getDayLabel(d)).join(', ');
}

/**
 * Map from day string to DaysOfWeekFlags.
 */
const DAY_STRING_TO_FLAG: Record<string, DaysOfWeekFlags> = {
	monday: DaysOfWeekFlags.Monday,
	tuesday: DaysOfWeekFlags.Tuesday,
	wednesday: DaysOfWeekFlags.Wednesday,
	thursday: DaysOfWeekFlags.Thursday,
	friday: DaysOfWeekFlags.Friday,
	saturday: DaysOfWeekFlags.Saturday,
	sunday: DaysOfWeekFlags.Sunday,
};

/**
 * Map from DaysOfWeekFlags to day string.
 */
const FLAG_TO_DAY_STRING: Record<DaysOfWeekFlags, string> = {
	[DaysOfWeekFlags.Monday]: 'monday',
	[DaysOfWeekFlags.Tuesday]: 'tuesday',
	[DaysOfWeekFlags.Wednesday]: 'wednesday',
	[DaysOfWeekFlags.Thursday]: 'thursday',
	[DaysOfWeekFlags.Friday]: 'friday',
	[DaysOfWeekFlags.Saturday]: 'saturday',
	[DaysOfWeekFlags.Sunday]: 'sunday',
	[DaysOfWeekFlags.None]: '',
};

/**
 * Converts DaysOfWeekFlags to an array of day strings for use with MultiCheckbox.
 */
export function flagsToStringArray(flags: DaysOfWeekFlags): string[] {
	return getSelectedDays(flags).map(d => FLAG_TO_DAY_STRING[d]);
}

/**
 * Converts an array of day strings to DaysOfWeekFlags.
 */
export function stringArrayToFlags(days: string[]): DaysOfWeekFlags {
	return days.reduce((acc, day) => {
		const flag = DAY_STRING_TO_FLAG[day.toLowerCase()];
		// eslint-disable-next-line no-bitwise
		return flag ? acc | flag : acc;
	}, DaysOfWeekFlags.None);
}

/**
 * Helper function to get the price adjustment display string.
 */
export function formatPriceAdjustment(type: priceAdjustmentType, value: number): string {
	if (type === 'NONE' || value === 0) {
		return 'No adjustment';
	}

	const sign = value >= 0 ? '+' : '';

	if (type === 'FIXED') {
		return `${sign}$${value.toFixed(2)}`;
	}

	if (type === 'PERCENTAGE') {
		return `${sign}${value}%`;
	}

	return 'No adjustment';
}
